import { useSelector } from '@xstate/react';
import { useCallback } from 'react';
import { CdsLicenseFormValues } from '../cds/types';
import { selectCdsLicenseForm, selectCdsLicenses, selectLoading } from '../selectors';
import { useLicensesActorRef } from '../utils';

/**
 * Service hook for CDS license tab.
 * Subscribes to CDS-specific slices and shared loading state.
 * DEA license data changes don't trigger re-renders, but shared loading transitions may.
 */
export function useCdsService() {
  const licensesRef = useLicensesActorRef();

  // ── Narrow selectors ────────────────────────────────────────────────
  const cdsLicenses = useSelector(licensesRef, selectCdsLicenses);
  const cdsLicenseForm = useSelector(licensesRef, selectCdsLicenseForm);
  const loading = useSelector(licensesRef, selectLoading);

  // ── Stable callbacks ────────────────────────────────────────────────
  const triggerAdd = useCallback(() => licensesRef.send({ type: 'ON_ADD_LICENSE' }), [licensesRef]);

  const handleEditClick = useCallback(
    (id: number) => licensesRef.send({ type: 'ON_EDIT_LICENSE', id }),
    [licensesRef]
  );

  const handleDeleteClick = useCallback(
    (id: number) => licensesRef.send({ type: 'ON_SEND_DELETE_CONFIRMATION', payload: { id } }),
    [licensesRef]
  );

  const sendLicenseCreate = useCallback(
    (values: CdsLicenseFormValues) =>
      licensesRef.send({ type: 'ON_SUBMIT_LICENSE_CREATE', values }),
    [licensesRef]
  );

  const sendLicenseUpdate = useCallback(
    (values: CdsLicenseFormValues) =>
      licensesRef.send({ type: 'ON_SUBMIT_LICENSE_UPDATE', values }),
    [licensesRef]
  );

  const onCancel = useCallback(
    () => licensesRef.send({ type: 'ON_CANCEL_EDIT_OR_CREATE' }),
    [licensesRef]
  );

  return {
    cdsLicenses,
    cdsLicenseForm,
    loading,
    triggerAdd,
    handleEditClick,
    handleDeleteClick,
    sendLicenseCreate,
    sendLicenseUpdate,
    onCancel,
  };
}
